// BUGS.md T24: a matter's own page shows what the buttons that lead to it promise ("Read what was sent about <matter>",
// "Open <matter> and its N open commitments", the item page's "Open the matter"), in the order Mike chose by pop-up on
// 6 Oct ("Yes, in that order"): its open commitments, then its mail newest first, then its tasks and its deal, each
// opening its own page. Drawn under the matter's fields (SimpleRecord); what the reader may not see is not listed.
import { CardFrame } from '../parts/index.js';
import type { ReactNode } from 'react';
import { records, type MatterContents as Contents } from '../api.js';
import { opensPath } from '../deals/live-words.js';
import { formatDate } from '../labels.js';
import { Failed, Loading, useLoad } from './shared.js';

const STATUS: Record<string, string> = { suggested: 'Cobrain’s reading', accepted: 'Accepted' };

function Line({ path, open, children }: { path: string | null; open: (path: string) => void; children: ReactNode }) {
  return <li>{path ? <a href={path} onClick={(e) => { e.preventDefault(); open(path); }}>{children}</a> : children}</li>;
}

export function MatterContents({ store, id, open }: { store: string; id: string; open: (path: string) => void }) {
  const [state] = useLoad<Contents>(() => records.matterContents(store, id), [store, id]);
  if (state.kind === 'loading') return <Loading what="what is on the matter" />;
  if (state.kind === 'error') return <Failed message={state.message} />;
  const c = state.data;
  const due = (d: string | null) => (d ? ` · due ${formatDate(d)}` : '');
  return (
    <div className="matter-contents">
      <CardFrame variant="panel" >
        <h3>Open commitments</h3>
        {c.commitments.length === 0 ? <p className="note">None open.</p> : (
          <ul className="matter-list">
            {c.commitments.map((x) => (
              <Line key={x.id} open={open} path={x.taskId ? `/tasks/${encodeURIComponent(x.taskId)}` : x.itemId ? `/items/${encodeURIComponent(x.itemId)}?store=${encodeURIComponent(store)}` : null}>
                {x.statement}<span className="note">{x.byName ? ` · ${x.byName}` : ''}{x.toName ? ` to ${x.toName}` : ''}{due(x.dueAt)} · {STATUS[x.status] ?? x.status}</span>
              </Line>
            ))}
          </ul>
        )}
      </CardFrame>
      <CardFrame variant="panel" >
        <h3>Mail, newest first</h3>
        {c.mail.length === 0 ? <p className="note">Nothing filed under it that you may read.</p> : (
          <ul className="matter-list">
            {c.mail.map((m) => (
              <Line key={m.signalId} open={open} path={opensPath(m.signalId, m.opens, store)}>
                {m.subject || '(no subject)'}<span className="note">{m.from ? ` · ${m.from}` : ''} · {formatDate(m.at)}</span>
              </Line>
            ))}
          </ul>
        )}
        {c.moreMail ? <p className="note">More is filed under it than the {c.mail.length} newest shown here.</p> : null}
      </CardFrame>
      <CardFrame variant="panel" >
        <h3>Tasks</h3>
        {c.tasks.length === 0 ? <p className="note">None open.</p> : (
          <ul className="matter-list">
            {c.tasks.map((k) => (
              <Line key={k.id} open={open} path={`/tasks/${encodeURIComponent(k.id)}`}>
                {k.title}<span className="note">{k.ownerName ? ` · ${k.ownerName}` : ''}{due(k.dueAt)}</span>
              </Line>
            ))}
          </ul>
        )}
        <h3>Deal</h3>
        {c.deal ? (
          <ul className="matter-list"><Line open={open} path={`/deals/${encodeURIComponent(c.deal.id)}`}>{c.deal.stage}<span className="note"> · {c.deal.lifecycle}</span></Line></ul>
        ) : <p className="note">No deal on this matter.</p>}
      </CardFrame>
    </div>
  );
}
